import { apiClient } from "@/services/api-client";
import type {
  CreateDishDto,
  DishDto,
  DishMicronutrientsResponse,
  DishesListParams,
  DishesListResponse,
  ExportDishToFoodResponseDto,
  GetCustomDishesParams,
  MyDishesParams,
  ToggleDishActiveDto,
  UpdateDishDto,
} from "./types";

// Re-export types and constants
export type {
  CreateDishDto,
  DishDto,
  DishIngredientDto,
  DishIngredientInput,
  DishMicronutrientsResponse,
  DishesListParams,
  DishesListResponse,
  ExportDishToFoodResponseDto,
  GetCustomDishesParams,
  Micronutrients,
  MyDishesParams,
  UpdateDishDto,
} from "./types.d";

export { CUISINE_TYPES, DIFFICULTY_LEVELS, MEAL_TYPES } from "./types.d";

const API_BASE = "/dishes";

/**
 * Dishes API Service
 * Quản lý món ăn với đầy đủ CRUD operations
 */
export const dishesApi = {
  /**
   * Tạo món ăn mới
   * POST /api/dishes
   */
  create: async (data: CreateDishDto): Promise<DishDto> => {
    return apiClient.post<DishDto>(`${API_BASE}`, data);
  },

  /**
   * Lấy danh sách món ăn với filter
   * GET /api/dishes
   */
  getList: async (params?: DishesListParams): Promise<DishesListResponse> => {
    return apiClient.get<DishesListResponse>(`${API_BASE}`, params);
  },

  /**
   * Lấy món ăn của user hiện tại
   * GET /api/dishes/my-dishes
   */
  getMyDishes: async (params?: MyDishesParams): Promise<DishesListResponse> => {
    return apiClient.get<DishesListResponse>(`${API_BASE}/my-dishes`, params);
  },

  /**
   * Lấy món ăn theo ID
   * GET /api/dishes/:id
   */
  getById: async (id: string): Promise<DishDto> => {
    return apiClient.get<DishDto>(`${API_BASE}/${id}`);
  },

  /**
   * Cập nhật món ăn
   * PUT /api/dishes/:id
   */
  update: async (id: string, data: UpdateDishDto): Promise<DishDto> => {
    return apiClient.put<DishDto>(`${API_BASE}/${id}`, data);
  },

  /**
   * Xóa món ăn
   * DELETE /api/dishes/:id
   */
  delete: async (id: string): Promise<{ message: string }> => {
    return apiClient.delete<{ message: string }>(`${API_BASE}/${id}`);
  },

  /**
   * Toggle trạng thái public của món ăn
   * PATCH /api/dishes/:id/toggle-public
   */
  togglePublic: async (id: string): Promise<DishDto> => {
    return apiClient.patch<DishDto>(`${API_BASE}/${id}/toggle-public`);
  },

  /**
   * Toggle trạng thái verified của món ăn
   * PATCH /api/dishes/:id/toggle-verified
   */
  toggleVerified: async (id: string): Promise<DishDto> => {
    return apiClient.patch<DishDto>(`${API_BASE}/${id}/toggle-verified`);
  },

  /**
   * Toggle trạng thái active của món ăn (ADMIN)
   * PUT /dishes/admin/:id/toggle-active
   * Cho phép gửi kèm adminNote để lưu lý do ẩn/hiển
   */
  toggleActive: async (
    id: string,
    data?: ToggleDishActiveDto
  ): Promise<DishDto> => {
    return apiClient.put<DishDto>(
      `/dishes/admin/${id}/toggle-active`,
      data || {}
    );
  },

  /**
   * Cập nhật điểm phổ biến
   * PATCH /api/dishes/:id/popularity-score
   */
  updatePopularityScore: async (
    id: string,
    score: number
  ): Promise<DishDto> => {
    return apiClient.patch<DishDto>(`${API_BASE}/${id}/popularity-score`, {
      popularityScore: score,
    });
  },

  /**
   * Tính toán lại dinh dưỡng từ nguyên liệu
   * POST /api/dishes/:id/recalculate-nutrition
   */
  recalculateNutrition: async (id: string): Promise<DishDto> => {
    return apiClient.post<DishDto>(`${API_BASE}/${id}/recalculate-nutrition`);
  },

  /**
   * Lấy thông tin vi chất dinh dưỡng của món ăn
   * GET /api/dishes/:id/micronutrients
   */
  getMicronutrients: async (
    id: string
  ): Promise<DishMicronutrientsResponse> => {
    return apiClient.get<DishMicronutrientsResponse>(
      `${API_BASE}/${id}/micronutrients`
    );
  },

  /**
   * Export dish to food (ADMIN)
   * POST /dishes/admin/:id/export-to-food
   * Không có body; yêu cầu quyền ADMIN
   */
  exportToFood: async (id: string): Promise<ExportDishToFoodResponseDto> => {
    return apiClient.post<ExportDishToFoodResponseDto>(
      `/dishes/admin/${id}/export-to-food`
    );
  },

  /**
   * Lấy tất cả Custom Dishes (Admin Only)
   * GET /dishes/admin/custom-dishes
   *
   * @param params Query parameters cho pagination và filtering
   * @returns Promise<DishesListResponse>
   *
   * @example
   * ```typescript
   * // Lấy tất cả custom dishes
   * const result = await dishesApi.getCustomDishes();
   *
   * // Lấy với filter theo userId
   * const userDishes = await dishesApi.getCustomDishes({ userId: "user-uuid" });
   *
   * // Lấy với sort và pagination
   * const sortedDishes = await dishesApi.getCustomDishes({
   *   page: 1,
   *   take: 20,
   *   sortBy: "name",
   *   order: "ASC"
   * });
   * ```
   */
  getCustomDishes: async (
    params: GetCustomDishesParams = {}
  ): Promise<DishesListResponse> => {
    const {
      page = 1,
      take = 10,
      order = "ASC",
      userId,
      sortBy = "createdAt",
    } = params;

    const queryParams: Record<string, any> = {
      page,
      take,
      order,
      sortBy,
    };

    // Add optional filter
    if (userId) queryParams.userId = userId;

    return apiClient.get<DishesListResponse>(
      `${API_BASE}/admin/custom-dishes`,
      queryParams
    );
  },

  /**
   * Lấy danh sách dishes có chứa foodId trong ingredients
   * GET /dishes/by-food/:foodId
   *
   * @param foodId - UUID của food cần tìm trong ingredients
   * @param params - Query parameters cho pagination và sorting
   * @returns Promise<DishesListResponse>
   *
   * @example
   * ```typescript
   * // Lấy dishes chứa foodId
   * const result = await dishesApi.getDishesByFoodId("food-uuid");
   *
   * // Với pagination và sorting
   * const result = await dishesApi.getDishesByFoodId("food-uuid", {
   *   page: 1,
   *   take: 20,
   *   sortBy: "name",
   *   order: "ASC"
   * });
   * ```
   */
  getDishesByFoodId: async (
    foodId: string,
    params: DishesListParams = {}
  ): Promise<DishesListResponse> => {
    const {
      page = 1,
      take = 10,
      order = "ASC",
      sortBy = "createdAt",
    } = params;

    const queryParams: Record<string, any> = {
      page,
      take,
      order,
      sortBy,
    };

    return apiClient.get<DishesListResponse>(
      `${API_BASE}/by-food/${foodId}`,
      queryParams
    );
  },
};

export default dishesApi;
